import React, { useState, useEffect, useMemo } from 'react';
import { ordersApi, schemesApi } from '@/services/api';
import { CurrencyCode, formatDate, formatNumber } from '@/utils/formatters';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { SCHEME_REGION_DOMESTIC, SCHEME_REGION_GLOBAL } from '@/config/constants';
import { getCellClass, formatSignedNumber } from '@/components/utils/PerformanceCell';
import PerformanceLegend from '@/components/utils/PerformanceLegend';
import { FilterOption } from '@/components/utils/filters';
import { Button, FilterSelect, FilterMultiSelect } from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import styles from './StockwisePerformance.module.css';

// Define the type for stock-wise performance data based on the backend response
interface StockPerformanceData {
    base_currency: string;
    company_id: number;
    name: string;
    symbol: string;
    unit_cost: number;
    unit_price: number;
    total_quantity: number;
    current_price: number;
    market_value_cr: number | null;
    percentage_of_aum: number | null;
    total_change_cr: number | null;
    total_change_percent: number | null;
    today_change_cr: number | null;
    today_change_percent: number | null;
}

interface StockPerformanceResponse {
    status: string;
    holdings_date: string;
    total_aum: number;
    data: StockPerformanceData[];
}

interface Scheme {
    id: string;
    code: string;
    acronym: string;
    display_name: string;
    full_name: string;
    region: number; // 1 for domestic, 2 for global
}

type SortDirection = 'asc' | 'desc';

const COMPACT_CONTENT_IN_STOCKWISE_PERFORMANCE_TABLE = true;

// The API reports market value and changes in crores. Domestic shows them as is; global
// (USD) schemes show them in thousands, since crores are not a dollar unit.
const THOUSANDS_PER_CRORE = 10_000;
const isUsdBase = (baseCurrency: string): boolean => baseCurrency.toUpperCase() === CurrencyCode.USD;
const amountUnitFor = (baseCurrency: string): string => (isUsdBase(baseCurrency) ? 'k' : 'Cr');
const formatCroreAmount = (valueInCrores: number | null, baseCurrency: string): string => {
    if (valueInCrores === null || valueInCrores === undefined || isNaN(valueInCrores)) return 'N/A';
    if (!isUsdBase(baseCurrency)) return formatNumber(valueInCrores, 2);
    // Western grouping for a dollar figure: "150,000.00", not "1,50,000.00".
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        .format(valueInCrores * THOUSANDS_PER_CRORE);
};

const DEFAULT_SCHEME_CODES: Record<'domestic' | 'global', string[]> = {
    domestic: ['CIEP', 'IEF', 'AIF_IEF'],
    global: ['SCPL', 'SIML'],
};

const REGION_OPTIONS: FilterOption[] = [
    { value: 'domestic', label: 'Domestic' },
    { value: 'global', label: 'Global' },
];

const StockWisePerformance: React.FC = () => {
    const [stockPerformanceData, setStockPerformanceData] = useState<StockPerformanceResponse | null>(null);
    const [selectedSchemeCodes, setSelectedSchemeCodes] = useState<string[]>([]);
    const [excludeInherited, setExcludeInherited] = useState<boolean>(true);
    const [stockSort, setStockSort] = useState<{ column: string; direction: SortDirection }>({ column: 'today_change_percent', direction: 'desc' });
    const [stockLoading, setStockLoading] = useState(false);
    const [stockError, setStockError] = useState<string | null>(null);
    const [availableSchemes, setAvailableSchemes] = useState<Scheme[]>([]);
    // Schemes load on mount (and pick the default selection), so start in the loading state.
    const [schemesLoading, setSchemesLoading] = useState(true);
    const [schemesError, setSchemesError] = useState<string | null>(null);
    const [regionFilter, setRegionFilter] = useState<'domestic' | 'global'>('domestic');
    const [searchQuery, setSearchQuery] = useState('');
    const [currencySymbol, setCurrencySymbol] = useState('');
    const [amountUnit, setAmountUnit] = useState('Cr');
    useEffect(() => {
        loadAvailableSchemes();
    }, []);

    useEffect(() => {
        if (selectedSchemeCodes.length > 0) {
            loadStockPerformanceData();
        } else {
            setStockPerformanceData(null);
        }
    }, [selectedSchemeCodes, excludeInherited]);

    const loadAvailableSchemes = async () => {
        try {
            setSchemesLoading(true);
            setSchemesError(null);
            const response = await schemesApi.getSchemes({ is_active: true, page_size: 100 });
            const schemes = response.results.map(scheme => ({
                id: String(scheme.id),
                code: (scheme.code || '') as string,
                acronym: (scheme.acronym || '') as string,
                display_name: (scheme.display_name || scheme.name || '') as string,
                full_name: (scheme.full_name || scheme.name || '') as string,
                region: Number(scheme.region)
            }));
            setAvailableSchemes(schemes);

            setSelectedSchemeCodes(defaultSchemeCodesFor(schemes, regionFilter));

        } catch (err: any) {
            setSchemesError(err.response?.data?.error || 'Failed to load schemes');
            console.error('Error loading schemes:', err);
        } finally {
            setSchemesLoading(false);
        }
    };

    const loadStockPerformanceData = async () => {
        try {
            setStockLoading(true);
            setStockError(null);
            const response = await ordersApi.getStockWisePerformance({
                scheme_codes: selectedSchemeCodes.join(','),
                exclude_inherited: excludeInherited ? 'true' : 'false'
            });
            setStockPerformanceData(response);
            if (response.data.length > 0) {
                setCurrencySymbol(getCurrencySymbol(response.data[0].base_currency));
                setAmountUnit(amountUnitFor(response.data[0].base_currency));
            }
        } catch (err: any) {
            setStockError(err.response?.data?.error || 'Failed to load stock-wise performance data');
            console.error('Error loading stock-wise performance data:', err);
        } finally {
            setStockLoading(false);
        }
    };

    /** The region's default schemes that exist among the loaded ones. */
    const defaultSchemeCodesFor = (schemes: Scheme[], region: 'domestic' | 'global'): string[] =>
        schemes.filter(s => DEFAULT_SCHEME_CODES[region].includes(s.code)).map(s => s.code);

    const handleSort = (column: string) => {
        if (stockPerformanceData && stockPerformanceData.data.length > 0) {
            setStockSort(prev => ({
                column,
                direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
            }));
        }
    };

    const filteredStockData = useMemo(() => {
        const data = stockPerformanceData?.data ?? [];
        if (!searchQuery) return data;
        const lowerQuery = searchQuery.toLowerCase();
        return data.filter(stockwisePerformance_row =>
            stockwisePerformance_row.symbol.toLowerCase().includes(lowerQuery) ||
            stockwisePerformance_row.name.toLowerCase().includes(lowerQuery)
        );
    }, [stockPerformanceData, searchQuery]);

    const sortedStockData = useMemo(() => {
        return [...filteredStockData].sort((a, b) => {
            let aVal: number | string = a[stockSort.column as keyof typeof a] ?? 0;
            let bVal: number | string = b[stockSort.column as keyof typeof b] ?? 0;

            // Handle null values
            if (aVal === null) aVal = stockSort.direction === 'asc' ? Infinity : -Infinity;
            if (bVal === null) bVal = stockSort.direction === 'asc' ? Infinity : -Infinity;

            // Convert to number if possible
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return stockSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
            } else {
                aVal = String(aVal).toLowerCase();
                bVal = String(bVal).toLowerCase();
                return stockSort.direction === 'asc'
                    ? aVal.localeCompare(bVal)
                    : bVal.localeCompare(aVal);
            }
        });
    }, [filteredStockData, stockSort]);

    const handleRefresh = () => {
        loadStockPerformanceData();
    };

    const handleDownloadCSV = () => {
        if (sortedStockData.length === 0) return;

        const currencySymbol = sortedStockData[0]?.base_currency ? getCurrencySymbol(sortedStockData[0].base_currency) : '';
        const amountUnit = amountUnitFor(sortedStockData[0]?.base_currency ?? '');
        const headers = [
            'Symbol',
            'Quantity',
            `Avg Cost (${currencySymbol})`,
            `Prev Close Price (${currencySymbol})`,
            `Current Price (${currencySymbol})`,
            `Market Value (${currencySymbol} ${amountUnit})`,
            '% of AUM',
            `Total Change (${currencySymbol} ${amountUnit})`,
            'Total Change (%)',
            `Today's Change (${currencySymbol} ${amountUnit})`,
            "Today's Change (%)",
        ];

        const rows = sortedStockData.map(stock => [
            stock.symbol,
            stock.total_quantity,
            formatCurrency(stock.unit_cost),
            formatCurrency(stock.unit_price),
            formatCurrency(stock.current_price),
            formatCroreAmount(stock.market_value_cr, stock.base_currency),
            stock.percentage_of_aum !== null ? stock.percentage_of_aum.toFixed(2) : 'N/A',
            formatCroreAmount(stock.total_change_cr, stock.base_currency),
            formatSignedNumber(stock.total_change_percent),
            formatCroreAmount(stock.today_change_cr, stock.base_currency),
            formatSignedNumber(stock.today_change_percent),
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
            .join('\n');

        const blob = new Blob(['\uFEFF', csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stockwise_performance_${stockPerformanceData?.holdings_date || 'data'}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const getCurrencySymbol = (currency: string): string => {
        switch (currency.toUpperCase()) {
            case CurrencyCode.INR:
                return '₹';
            case CurrencyCode.USD:
                return '$';
            default:
                return currency;
        }
    };

    /** Figures on this page are plain 2-dp numbers — the unit is in the header,
     *  so the cell never repeats the currency symbol. */
    const formatCurrency = (value: number | null): string =>
        value === null || value === undefined || isNaN(value) ? 'N/A' : formatNumber(value, 2);

    // Widths + shortened headers keep the 10 columns within a typical viewport.
    const columns = useMemo((): ColumnDef<StockPerformanceData>[] => {
        return [
            { key: 'symbol', header: 'Symbol', width: '100px', sortable: true, align: 'left',
              cellClassName: () => shared['cell-strong'],
              render: stockwisePerformance_row => stockwisePerformance_row.symbol },
            { key: 'total_quantity', header: 'Quantity', width: '90px', sortable: true, align: 'right', render: stockwisePerformance_row => Number(stockwisePerformance_row.total_quantity).toLocaleString() },
            { key: 'unit_cost', header: `Avg Cost (${currencySymbol})`, width: '100px', sortable: true, align: 'right', render: stockwisePerformance_row => formatCurrency(stockwisePerformance_row.unit_cost) },
            { key: 'unit_price', header: `Prev Close (${currencySymbol})`, width: '105px', sortable: true, align: 'right', render: stockwisePerformance_row => formatCurrency(stockwisePerformance_row.unit_price) },
            { key: 'current_price', header: `Curr. Price (${currencySymbol})`, width: '105px', sortable: true, align: 'right', render: stockwisePerformance_row => formatCurrency(stockwisePerformance_row.current_price) },
            { key: 'market_value_cr', header: `Mkt Value (${currencySymbol} ${amountUnit})`, width: '115px', sortable: true, align: 'right', render: stockwisePerformance_row => formatCroreAmount(stockwisePerformance_row.market_value_cr, stockwisePerformance_row.base_currency) },
            { key: 'percentage_of_aum', header: '% AUM', width: '80px', sortable: true, align: 'right', render: stockwisePerformance_row => stockwisePerformance_row.percentage_of_aum !== null ? stockwisePerformance_row.percentage_of_aum.toFixed(2) : 'N/A' },
            { key: 'total_change_cr', header: `Total Chg (${currencySymbol} ${amountUnit})`, width: '110px', sortable: true, align: 'right', cellClassName: stockwisePerformance_row => getCellClass(stockwisePerformance_row.total_change_cr), render: stockwisePerformance_row => formatCroreAmount(stockwisePerformance_row.total_change_cr, stockwisePerformance_row.base_currency)},
            { key: 'total_change_percent', header: 'Total Chg (%)', width: '95px', sortable: true, align: 'right', cellClassName: stockwisePerformance_row => getCellClass(stockwisePerformance_row.total_change_percent), render: stockwisePerformance_row => formatSignedNumber(stockwisePerformance_row.total_change_percent)},
            { key: 'today_change_cr', header: `Today Chg (${currencySymbol} ${amountUnit})`, width: '110px', sortable: true, align: 'right', cellClassName: stockwisePerformance_row => getCellClass(stockwisePerformance_row.today_change_cr), render: stockwisePerformance_row => formatCroreAmount(stockwisePerformance_row.today_change_cr, stockwisePerformance_row.base_currency)},
            { key: 'today_change_percent', header: `Today Chg (%)`, width: '95px', sortable: true, align: 'right', cellClassName: stockwisePerformance_row => getCellClass(stockwisePerformance_row.today_change_percent), render: stockwisePerformance_row => formatSignedNumber(stockwisePerformance_row.today_change_percent)},
        ];
    }, [currencySymbol, amountUnit]);

    /** Schemes the region filter allows, as select options. */
    const schemeOptions: FilterOption[] = useMemo(
        () => availableSchemes
            .filter(scheme => (regionFilter === 'domestic' ? scheme.region === SCHEME_REGION_DOMESTIC : scheme.region === SCHEME_REGION_GLOBAL))
            .map(scheme => ({
                value: scheme.code,
                label: `${scheme.code}${scheme.display_name ? ` - ${scheme.display_name}` : ''}`,
            })),
        [availableSchemes, regionFilter],
    );

    const selectedSchemeOptions: FilterOption[] = useMemo(
        () => selectedSchemeCodes.map(code => {
            const scheme = availableSchemes.find(s => s.code === code);
            return {
                value: code,
                label: scheme ? `${scheme.code}${scheme.display_name ? ` - ${scheme.display_name}` : ''}` : code,
            };
        }),
        [selectedSchemeCodes, availableSchemes],
    );

    /** Switching region replaces the selection with that region's default schemes. */
    const handleRegionChange = (region: 'domestic' | 'global') => {
        setRegionFilter(region);
        setSelectedSchemeCodes(defaultSchemeCodesFor(availableSchemes, region));
    };

    const filterSlot = (
        <>
            <FilterSelect
                label="Region"
                value={REGION_OPTIONS.find(o => o.value === regionFilter) ?? null}
                onChange={opt => handleRegionChange((opt?.value ?? 'domestic') as 'domestic' | 'global')}
                options={REGION_OPTIONS}
            />

            <FilterMultiSelect
                label="Schemes"
                value={selectedSchemeOptions}
                onChange={next => setSelectedSchemeCodes(next.map(o => o.value))}
                options={schemeOptions}
                loading={schemesLoading}
            />

            <label className={styles['check-filter']}>
                <input
                    type="checkbox"
                    checked={excludeInherited}
                    onChange={e => setExcludeInherited(e.target.checked)}
                />
                Exclude inherited
            </label>
        </>
    );

    const combinedError = [stockError, schemesError].filter(Boolean).join(' | ') || null;
    // Also covers the render between the default schemes being selected and the stock
    // fetch starting, so the table never flashes its empty message on open.
    const showLoader = stockLoading || schemesLoading
        || (selectedSchemeCodes.length > 0 && !stockPerformanceData && !stockError);

    return (
        <DataTable
            title="Stockwise Performance"
            subtitle={stockPerformanceData?.holdings_date
                ? `Positions across the selected schemes, as of ${formatDate(stockPerformanceData.holdings_date)}.`
                : 'Positions across the selected schemes, with their move today and since purchase.'}
            headerActions={
                <Button variant="primary" onClick={handleRefresh} disabled={stockLoading || schemesLoading}>
                    {stockLoading || schemesLoading ? 'Loading…' : 'Refresh'}
                </Button>
            }
            data={sortedStockData}
            columns={columns}
            rowKey={row => row.company_id}
            totalCount={sortedStockData.length}
            loading={showLoader}
            loadingMessage="Loading stockwise performance…"
            error={combinedError}
            currentPage={1}
            totalPages={1}
            onPageChange={() => undefined}
            pageSize={Math.max(sortedStockData.length, 10)}
            sortField={stockSort.column}
            sortDir={stockSort.direction}
            onSort={handleSort}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search by symbol or name…"
            filterSlot={filterSlot}
            activeFilterCount={selectedSchemeCodes.length > 0 ? 1 : 0}
            onClearFilters={() => setSelectedSchemeCodes([])}
            isDownloading={showLoader}
            onDownloadCSV={sortedStockData.length > 0 ? handleDownloadCSV : undefined}
            compact={COMPACT_CONTENT_IN_STOCKWISE_PERFORMANCE_TABLE}
            fitContent
            footerSlot={<PerformanceLegend />}
            emptyMessage={selectedSchemeCodes.length === 0
                ? 'Select at least one scheme to view stock performance.'
                : 'No stock performance data available.'}
        />
    );
};

export default StockWisePerformance;
